import { useEffect, useState } from "react";
import { Loader2, DollarSign, MapPin, RefreshCw, CheckCircle2, XCircle, ExternalLink, Users } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

export default function AdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/admin/jobs").then((r) => setJobs(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggleStatus = async (slug, isOpen) => {
    const status = isOpen ? "open" : "closed";
    setBusy(slug);
    try {
      const { data } = await api.patch(`/admin/jobs/${slug}`, { status });
      setJobs((js) => js.map((j) => (j.slug === slug ? { ...j, ...data } : j)));
      toast.success(status === "open" ? "Posting is now LIVE" : "Posting closed & de-listed from Google");
    } catch { toast.error("Update failed"); }
    finally { setBusy(""); }
  };

  const renew = async (slug) => {
    setBusy(slug);
    try {
      const { data } = await api.patch(`/admin/jobs/${slug}`, { renew: true });
      setJobs((js) => js.map((j) => (j.slug === slug ? { ...j, ...data } : j)));
      toast.success("Posting renewed — dates refreshed for Google");
    } catch { toast.error("Renew failed"); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-extrabold">Job Postings</h1>
        <p className="text-muted-foreground">Open or close roles. Closing a posting instantly removes it from your careers pages and its Google Jobs listing.</p>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : jobs.length === 0 ? (
        <div className="bg-card border border-border p-12 text-center text-muted-foreground">No job postings found.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {jobs.map((j) => {
            const isOpen = j.status === "open";
            return (
              <div key={j.slug} className="bg-card border border-border p-6" data-testid={`admin-job-${j.slug}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="font-heading text-xl font-bold">{j.title}</h3>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1.5"><DollarSign className="h-4 w-4 text-primary" /> ${j.salary_min}–${j.salary_max}/hr</span>
                      <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-primary" /> {j.location_city}, {j.location_state}</span>
                      <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-primary" /> {j.application_count} applied</span>
                    </div>
                  </div>
                  <span className={`flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-sm shrink-0 ${isOpen ? "bg-green-100 text-green-700" : "bg-muted text-muted-foreground"}`}>
                    {isOpen ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                    {isOpen ? "LIVE" : "CLOSED"}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 text-sm bg-muted/50 p-3 rounded-sm">
                  <div><span className="text-xs font-bold text-muted-foreground uppercase block">Posted</span>{fmtDate(j.date_posted)}</div>
                  <div><span className="text-xs font-bold text-muted-foreground uppercase block">Expires</span>{fmtDate(j.valid_through)}</div>
                </div>

                <div className="mt-5 flex items-center justify-between gap-3">
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <Switch checked={isOpen} disabled={busy === j.slug} onCheckedChange={(v) => toggleStatus(j.slug, v)} data-testid={`admin-job-toggle-${j.slug}`} />
                    <span className="text-sm font-semibold">{isOpen ? "Accepting applications" : "Not accepting"}</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" className="rounded-sm" disabled={busy === j.slug || !isOpen} onClick={() => renew(j.slug)} data-testid={`admin-job-renew-${j.slug}`}>
                      <RefreshCw className="h-4 w-4 mr-1.5" /> Renew
                    </Button>
                    <a href={`/careers/${j.slug}`} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary p-2" title="View page"><ExternalLink className="h-4 w-4" /></a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-primary/5 border border-primary/20 p-5 rounded-sm text-sm text-muted-foreground">
        <strong className="text-foreground">Tip:</strong> When a role is filled, flip it to <em>Closed</em> — Google requires expired job postings to be taken down, and this does it instantly. Use <em>Renew</em> to refresh the posted date on a role that's been open a while, which keeps it looking fresh in Google Jobs.
      </div>
    </div>
  );
}
